/**
 * Adjacency lookup for trim miter detection.
 *
 * Corner classification (ArchitecturalTrim.getCornerType) needs three
 * consecutive points — the far end of the incoming run, the shared corner,
 * and the far end of the outgoing run. Trim is generated one wall at a time,
 * so the two far points have to be recovered from the neighbouring walls.
 */

/** Endpoints within this distance (metres) are treated as a shared corner. */
export const CORNER_MATCH_TOLERANCE = 0.05;

const touches = (a, b, tolerance) =>
  Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.z - b.z, 2)) < tolerance;

/**
 * The walls that meet this one at its corners — the one rule for "shares a
 * corner", used by the trim mitre (`findAdjacentCorners`) and by Edit wall's
 * corner height matching (`wallRegionSnapLines`).
 *
 * A neighbouring wall may meet this one at EITHER of its own endpoints:
 * each wall's corner order comes from its own `rotation.y` (see
 * useWallTrim's extractWallCorners) and nothing normalises that to a room
 * traversal direction. All four endpoint pairings therefore have to be
 * checked — matching only end→start and start→end left ~3% of the joints in
 * the reference scan corpus invisible, and those outside corners were
 * silently never mitred, so the trim just stopped flat at the corner.
 *
 * Whichever way a neighbour is stored, `far` is its FAR endpoint (the one not
 * on the shared corner) and `shared` says which of ITS OWN ends is on it.
 *
 * @param {Array<{x:number,z:number}>} corners - This wall's corners
 * @param {string} wallId - This wall's id, skipped in the lookup
 * @param {Map<string, Array<{x:number,z:number}>>} wallCornersMap - Corners
 *   of every candidate wall, keyed by wall id
 * @param {number} tolerance - Endpoint match distance
 * @returns {{prev: {id, far, shared: 'start'|'end'}|null,
 *   next: {id, far, shared: 'start'|'end'}|null}} the walls meeting this
 *   wall's start (`prev`) and end (`next`)
 */
export function matchCornerNeighbours(
  corners,
  wallId,
  wallCornersMap,
  tolerance = CORNER_MATCH_TOLERANCE
) {
  if (!corners || corners.length < 2 || !wallCornersMap) {
    return { prev: null, next: null };
  }

  const startPoint = corners[0];
  const endPoint = corners[corners.length - 1];

  let prev = null;
  let next = null;

  wallCornersMap.forEach((otherCorners, otherId) => {
    if (otherId === wallId || !otherCorners || otherCorners.length < 2) return;

    const otherStart = otherCorners[0];
    const otherEnd = otherCorners[otherCorners.length - 1];

    // Neighbour meeting our START: the third point is its far end.
    if (prev === null) {
      if (touches(otherEnd, startPoint, tolerance)) {
        prev = { id: otherId, far: otherStart, shared: 'end' };
      } else if (touches(otherStart, startPoint, tolerance)) {
        prev = { id: otherId, far: otherEnd, shared: 'start' };
      }
    }

    // Neighbour meeting our END: likewise, its far end.
    if (next === null) {
      if (touches(otherStart, endPoint, tolerance)) {
        next = { id: otherId, far: otherEnd, shared: 'start' };
      } else if (touches(otherEnd, endPoint, tolerance)) {
        next = { id: otherId, far: otherStart, shared: 'end' };
      }
    }
  });

  return { prev, next };
}

/**
 * Find the points before and after a wall, for miter detection: the far
 * endpoints of the walls `matchCornerNeighbours` finds at each end.
 *
 * @param {Array<{x:number,z:number}>} corners - This wall's corners
 * @param {string} wallId - This wall's id, skipped in the lookup
 * @param {Map<string, Array<{x:number,z:number}>>} wallCornersMap - Corners
 *   of every wall registered for trim, keyed by wall id
 * @param {number} tolerance - Endpoint match distance
 * @returns {{prevCorner: Object|null, nextCorner: Object|null}}
 */
export function findAdjacentCorners(
  corners,
  wallId,
  wallCornersMap,
  tolerance = CORNER_MATCH_TOLERANCE
) {
  const { prev, next } = matchCornerNeighbours(
    corners,
    wallId,
    wallCornersMap,
    tolerance
  );
  return { prevCorner: prev?.far ?? null, nextCorner: next?.far ?? null };
}
